import { Link as RouterLink, useLocation } from "react-router-dom";
import React, { useEffect, useState, useRef } from 'react';
import { getAllCities, getCityUrl } from '../data/citiesData';

function Navbar() {
  const location = useLocation();
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showPresenceDropdown, setShowPresenceDropdown] = useState(false);
  const presenceDropdownRef = useRef(null);
  const dropdownTimeoutRef = useRef(null);
  const [showServicesDropdown, setShowServicesDropdown] = useState(false);
  const servicesDropdownRef = useRef(null);
  const servicesDropdownTimeoutRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);
  
  // Check if we're on the Why Choose Us page
  const isWhyChooseUsPage = location.pathname === '/why-choose-us' || location.pathname === '/why-choose-us/';

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const threshold = 200; // Higher threshold - only hide after scrolling more
      const scrollDifference = Math.abs(currentScrollY - lastScrollY);
      
      // Always show navbar when at the very top
      if (currentScrollY <= 50) {
        setIsNavbarVisible(true);
      }
      // Hide navbar when scrolling down past threshold (with minimum scroll distance)
      else if (currentScrollY > threshold && currentScrollY > lastScrollY && scrollDifference > 10) {
        setIsNavbarVisible(false);
      }
      // Show navbar when scrolling up (with minimum scroll distance)
      else if (currentScrollY < lastScrollY && scrollDifference > 10) {
        setIsNavbarVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    // Throttle scroll events for better performance
    let ticking = false;
    const throttledHandleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttledHandleScroll, { passive: true });
    return () => window.removeEventListener('scroll', throttledHandleScroll);
  }, [lastScrollY]);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Keep the existing gotop functionality
  useEffect(() => {
    const handleGotopScroll = () => {
      if (window.screen.width < 768 && window.scrollY > 690) {
        const gotop = document.querySelector(".gotop");
        if (gotop) gotop.classList.add("display");
      } else if (window.screen.width > 768 && window.scrollY > 220) {
        const gotop = document.querySelector(".gotop");
        if (gotop) gotop.classList.add("display");
      } else {
        const gotop = document.querySelector(".gotop");
        if (gotop) gotop.classList.remove("display");
      }
    };

    window.addEventListener('scroll', handleGotopScroll);
    return () => window.removeEventListener('scroll', handleGotopScroll);
  }, []);

  // Ensure menu is closed when component mounts or route changes
  useEffect(() => {
    const bar = document.querySelector(".bar");
    if (bar) {
      bar.classList.remove("opened");
      document.body.classList.remove("menu-open");
      document.documentElement.classList.remove("menu-open");
    }
  }, []);

  // Handle click outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (presenceDropdownRef.current && !presenceDropdownRef.current.contains(event.target)) {
        setShowPresenceDropdown(false);
      }
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(event.target)) {
        setShowServicesDropdown(false);
      }
    };

    if (showPresenceDropdown || showServicesDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPresenceDropdown, showServicesDropdown]);

  // Handle dropdown hover with delay to prevent accidental closing
  const handleMouseEnter = () => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current);
      dropdownTimeoutRef.current = null;
    }
    setShowPresenceDropdown(true);
  };

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setShowPresenceDropdown(false);
    }, 200); // 200ms delay before closing
  };

  // Handle Services dropdown hover with delay
  const handleServicesMouseEnter = () => {
    if (servicesDropdownTimeoutRef.current) {
      clearTimeout(servicesDropdownTimeoutRef.current);
      servicesDropdownTimeoutRef.current = null;
    }
    setShowServicesDropdown(true);
  };

  const handleServicesMouseLeave = () => {
    servicesDropdownTimeoutRef.current = setTimeout(() => {
      setShowServicesDropdown(false);
    }, 200); // 200ms delay before closing
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current);
      }
      if (servicesDropdownTimeoutRef.current) {
        clearTimeout(servicesDropdownTimeoutRef.current);
      }
    };
  }, []);

  function openBar() {
    const bar = document.querySelector(".bar");
    const isOpen = bar.classList.toggle("opened");
    
    // Prevent background scrolling when menu is open
    if (isOpen) {
      document.body.classList.add("menu-open");
      document.documentElement.classList.add("menu-open");
    } else {
      document.body.classList.remove("menu-open");
      document.documentElement.classList.remove("menu-open");
    }
  }
  
  function closeMenuOnLinkClick() {
    const bar = document.querySelector(".bar");
    // Close menu and remove scroll lock
    bar.classList.remove("opened");
    document.body.classList.remove("menu-open");
    document.documentElement.classList.remove("menu-open");
  }

  function scrollToSection(sectionId) {
    closeMenuOnLinkClick();

    const hashTarget = `#${sectionId}`;

    if (window.location.pathname !== '/') {
      window.location.href = `/${hashTarget}`;
      return;
    }

    if (window.location.hash !== hashTarget) {
      window.history.replaceState(null, '', hashTarget);
    }

    const scrollToElement = () => {
      const element =
        document.getElementById(sectionId) ||
        document.querySelector(`.${sectionId}`);

      if (element) {
        element.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    };

    requestAnimationFrame(() => {
      setTimeout(scrollToElement, 50);
    });
  }

  return (
    <nav 
      className={`navbar ${isWhyChooseUsPage ? 'navbar-why-choose-us' : ''}`}
      style={{
        opacity: isNavbarVisible ? 1 : 0,
        visibility: isNavbarVisible ? 'visible' : 'hidden',
        transition: 'opacity 0.4s ease-in-out, visibility 0.4s ease-in-out'
      }}
    >
      <div className="container">
        <div className="row">
          <h1 className="logo">
            <RouterLink
              to="/"
              style={{ cursor: "pointer", textDecoration: "none", color: "inherit" }}
            >
              Lunar Electric
            </RouterLink>
          </h1>
          <ul className="bar">
            <li>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('home');
                }}
                style={{ cursor: "pointer", textDecoration: "none", color: "white" }}
                title="Home - Lunar Electric"
              >
                Home
              </a>
            </li>
            <li 
              className="services-menu-item"
              ref={servicesDropdownRef}
              onMouseEnter={handleServicesMouseEnter}
              onMouseLeave={handleServicesMouseLeave}
            >
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  // On mobile, toggle dropdown instead of scrolling
                  if (isMobile) {
                    setShowServicesDropdown(!showServicesDropdown);
                  } else {
                    scrollToSection('services');
                  }
                }}
                style={{ cursor: "pointer", textDecoration: "none", color: "white" }}
                title="Solar Panel Installation Services"
                className="services-link"
              >
                Services
                {isMobile && (
                  <span className={`mobile-dropdown-arrow ${showServicesDropdown ? 'rotated' : ''}`}>▼</span>
                )}
              </a>
              {showServicesDropdown && (
                <div className="services-dropdown">
                  <div className="dropdown-content-wrapper">
                    <RouterLink
                      to="/utility-scale-solar"
                      className="dropdown-service-link"
                      onClick={closeMenuOnLinkClick}
                      style={{ fontSize: '12px' }}
                    >
                      Enterprise Power Solutions
                    </RouterLink>
                    <RouterLink
                      to="/solar-cold-storage"
                      className="dropdown-service-link"
                      onClick={closeMenuOnLinkClick}
                      style={{ fontSize: '12px' }}
                    >
                      Rooftop Solar for Cold Storage
                    </RouterLink>
                    <RouterLink
                      to="/solar-warehouses"
                      className="dropdown-service-link"
                      onClick={closeMenuOnLinkClick}
                      style={{ fontSize: '12px' }}
                    >
                      Rooftop Solar for Warehouses
                    </RouterLink>
                    <RouterLink
                      to="/commercial-solar"
                      className="dropdown-service-link"
                      onClick={closeMenuOnLinkClick}
                      style={{ fontSize: '12px' }}
                    >
                      Rooftop Solar for Commercial Buildings
                    </RouterLink>
                    <RouterLink
                      to="/residential-solar"
                      className="dropdown-service-link"
                      onClick={closeMenuOnLinkClick}
                      style={{ fontSize: '12px' }}
                    >
                      Rooftop Solar for Houses
                    </RouterLink>
                  </div>
                </div>
              )}
            </li>
            <li>
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection('about-scroll');
                }}
                style={{ cursor: "pointer", textDecoration: "none", color: "white" }}
                title="About Lunar Electric"
              >
                About
              </a>
            </li>
            <li>
              <RouterLink
                onClick={closeMenuOnLinkClick}
                to="/blogs"
                title="Solar Energy Blogs and Articles"
              >
                Blogs
              </RouterLink>
            </li>
            <li 
              className="presence-menu-item"
              ref={presenceDropdownRef}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <RouterLink
                onClick={(e) => {
                  closeMenuOnLinkClick();
                  // Allow navigation on click
                }}
                to="/our-presence"
                title="Our Presence - Cities We Serve"
                className="presence-link"
              >
                Our Presence
              </RouterLink>
              {showPresenceDropdown && (
                <div className="presence-dropdown">
                  <div className="dropdown-content-wrapper">
                    <div className="dropdown-cities">
                      {getAllCities().map(({ city, state }) => (
                        <RouterLink
                          key={`${state}-${city}`}
                          to={getCityUrl(city)}
                          className="dropdown-city-link"
                          onClick={closeMenuOnLinkClick}
                        >
                          <span className="city-name">{city}</span>
                        </RouterLink>
                      ))}
                    </div>
                    <div className="dropdown-footer">
                      <RouterLink
                        to="/our-presence"
                        onClick={closeMenuOnLinkClick}
                        className="view-all-link"
                      >
                        View All Cities →
                      </RouterLink>
                    </div>
                  </div>
                </div>
              )}
            </li>
            <li>
              <RouterLink
                onClick={closeMenuOnLinkClick}
                to="/solar-calculator"
                title="Free Solar Panel Cost Calculator"
              >
                Solar Calculator
              </RouterLink>
            </li>
          </ul>
          <div className="button" onClick={openBar}>
            <div className="burger"></div>
            <div className="burger"></div>
            <div className="burger"></div>
          </div>
          {/* Close button for mobile menu - only render on mobile */}
          {isMobile && (
            <div className="close-menu-button" onClick={closeMenuOnLinkClick}>
              <span className="close-icon">×</span>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
}
export default Navbar;
